import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertTriangle, TrendingDown, Eye, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';

const ProductGovernanceDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedQuadrant, setSelectedQuadrant] = useState('ALL');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Use available endpoints to build governance view
      const [productsRes, lifecycleRes] = await Promise.all([
        fetch(`${API_URL}/api/trm/products`),
        fetch(`${API_URL}/api/trm/stats/lifecycle`)
      ]);

      if (!productsRes.ok || !lifecycleRes.ok) throw new Error('Failed to fetch data');

      const productsData = await productsRes.json();
      const lifecycleData = await lifecycleRes.json();

      // Transform data to match expected format
      // Create quadrants based on product status and version count
      const products = productsData.products || [];
      const activeProducts = products.filter(p => p.record_status === 'Active');
      const retiredProducts = products.filter(p => p.record_status === 'Retired');

      // Simple quadrant logic: categorize by status and version count
      const avgVersions = products.reduce((sum, p) => sum + (p.version_count || 0), 0) / products.length;

      const result = {
        summary: {
          total_products: products.length,
          active_products: activeProducts.length,
          retired_products: retiredProducts.length,
          avg_versions: Math.round(avgVersions)
        },
        quadrants: {
          KEEP: activeProducts.filter(p => (p.version_count || 0) >= avgVersions).map(p => ({
            product_id: p.product_id,
            product_name: p.canonical_name,
            manufacturer: p.manufacturer_name,
            category: p.trm_category,
            version_count: p.version_count,
            status: p.record_status
          })),
          REPLACE: retiredProducts.map(p => ({
            product_id: p.product_id,
            product_name: p.canonical_name,
            manufacturer: p.manufacturer_name,
            category: p.trm_category,
            version_count: p.version_count,
            status: p.record_status
          })),
          REDUCE: activeProducts.filter(p => (p.version_count || 0) < avgVersions / 2).map(p => ({
            product_id: p.product_id,
            product_name: p.canonical_name,
            manufacturer: p.manufacturer_name,
            category: p.trm_category,
            version_count: p.version_count,
            status: p.record_status
          })),
          REVIEW: activeProducts.filter(p => {
            const vc = p.version_count || 0;
            return vc < avgVersions && vc >= avgVersions / 2;
          }).map(p => ({
            product_id: p.product_id,
            product_name: p.canonical_name,
            manufacturer: p.manufacturer_name,
            category: p.trm_category,
            version_count: p.version_count,
            status: p.record_status
          }))
        }
      };

      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Loading governance dashboard...</div>;
  if (error) return <div style={{ padding: '40px', color: 'var(--cares-orange)' }}>Error: {error}</div>;
  if (!data) return null;

  const { summary, quadrants } = data;

  const quadrantConfig = {
    'KEEP': {
      label: 'KEEP',
      color: 'var(--cares-green)',
      bgColor: '#ecfdf5',
      borderColor: 'var(--cares-green)',
      icon: CheckCircle,
      description: 'Healthy products - maintain current state'
    },
    'REPLACE': {
      label: 'REPLACE',
      color: 'var(--cares-orange)',
      bgColor: '#fef2f2',
      borderColor: 'var(--cares-orange)',
      icon: AlertTriangle,
      description: 'Deprecated or EOL - plan replacement'
    },
    'REDUCE': {
      label: 'REDUCE',
      color: 'var(--cares-amber)',
      bgColor: '#fffbeb',
      borderColor: 'var(--cares-amber)',
      icon: TrendingDown,
      description: 'Consolidation opportunity - reduce vendor count'
    },
    'REVIEW': {
      label: 'REVIEW',
      color: 'var(--cares-purple)',
      bgColor: '#eef2ff',
      borderColor: 'var(--cares-purple)',
      icon: Eye,
      description: 'Needs assessment - verify status and compliance'
    }
  };

  const filteredProducts = selectedQuadrant === 'ALL' 
    ? Object.values(quadrants).flat()
    : quadrants[selectedQuadrant] || [];

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--cares-text-2)', marginBottom: '8px' }}>
          Product Governance Dashboard
        </h1>
        <p style={{ fontSize: '14px', color: 'var(--cares-text-label)' }}>
          4-Quadrant Analysis: Strategic recommendations for portfolio management
        </p>
      </div>

      {/* Summary Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '20px',
        marginBottom: '32px'
      }}>
        {Object.entries(quadrantConfig).map(([key, config]) => {
          const Icon = config.icon;
          const count = summary[`${key.toLowerCase()}_count`];
          const percentage = ((count / summary.total_products) * 100).toFixed(1);

          return (
            <div
              key={key}
              onClick={() => setSelectedQuadrant(key)}
              style={{
                background: config.bgColor,
                borderLeft: `4px solid ${config.borderColor}`,
                borderRadius: '8px',
                padding: '20px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: selectedQuadrant === key ? '0 4px 6px rgba(0,0,0,0.1)' : '0 1px 3px rgba(0,0,0,0.1)',
                transform: selectedQuadrant === key ? 'translateY(-2px)' : 'none'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <Icon size={24} color={config.color} />
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--cares-text-2)' }}>{config.label}</h3>
              </div>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: config.color, marginBottom: '8px' }}>
                {count}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--cares-text-label)', marginBottom: '8px' }}>
                {percentage}% of portfolio
              </div>
              <div style={{ fontSize: '12px', color: 'var(--cares-text-3)', fontStyle: 'italic' }}>
                {config.description}
              </div>
            </div>
          );
        })}
      </div>

      {/* Filter Tabs */}
      <div style={{
        background: 'var(--cares-surface-card)',
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        display: 'flex',
        gap: '12px',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--cares-text-label)' }}>Filter:</span>
        <button
          onClick={() => setSelectedQuadrant('ALL')}
          style={{
            padding: '8px 16px',
            borderRadius: '6px',
            border: 'none',
            background: selectedQuadrant === 'ALL' ? 'var(--cares-blue)' : 'var(--cares-surface-board)',
            color: selectedQuadrant === 'ALL' ? 'var(--cares-text-max)' : 'var(--cares-text-label)',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer'
          }}
        >
          All Products ({summary.total_products})
        </button>
        {Object.entries(quadrantConfig).map(([key, config]) => (
          <button
            key={key}
            onClick={() => setSelectedQuadrant(key)}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: 'none',
              background: selectedQuadrant === key ? config.color : config.bgColor,
              color: selectedQuadrant === key ? 'var(--cares-text-max)' : config.color,
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            {key} ({summary[`${key.toLowerCase()}_count`]})
          </button>
        ))}
        
        <button
          onClick={() => alert('Export functionality coming soon!')}
          style={{
            marginLeft: 'auto',
            padding: '8px 16px',
            borderRadius: '6px',
            border: '1px solid var(--cares-line-strong)',
            background: 'var(--cares-surface-card)',
            color: 'var(--cares-text-label)',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          <Download size={16} />
          Export
        </button>
      </div>

      {/* Products Table */}
      <div style={{
        background: 'var(--cares-surface-card)',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--cares-surface-board)', borderBottom: '2px solid var(--cares-line-card)' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)' }}>QUADRANT</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)' }}>PRODUCT</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)' }}>MANUFACTURER</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)' }}>CATEGORY</th>
                <th style={{ padding: '12px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)' }}>VERSIONS</th>
                <th style={{ padding: '12px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)' }}>ACTIVE</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)' }}>RECOMMENDATION</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: 'var(--cares-text-note)' }}>
                    No products in this quadrant
                  </td>
                </tr>
              ) : (
                filteredProducts.map((product, idx) => {
                  const quadrant = Object.keys(quadrants).find(q => quadrants[q].includes(product));
                  const config = quadrantConfig[quadrant];
                  
                  return (
                    <tr key={idx} style={{ borderBottom: '1px solid var(--cares-line-card)' }}>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          background: config.bgColor,
                          color: config.color,
                          fontSize: '11px',
                          fontWeight: '600'
                        }}>
                          {quadrant}
                        </span>
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px', fontWeight: '500', color: 'var(--cares-text-2)' }}>{product.product_name}</td>
                      <td style={{ padding: '12px', fontSize: '14px', color: 'var(--cares-text-label)' }}>{product.manufacturer}</td>
                      <td style={{ padding: '12px', fontSize: '14px', color: 'var(--cares-text-label)' }}>{product.category || 'N/A'}</td>
                      <td style={{ padding: '12px', textAlign: 'center', fontSize: '14px', color: 'var(--cares-text-label)' }}>{product.version_count}</td>
                      <td style={{ padding: '12px', textAlign: 'center', fontSize: '14px', color: 'var(--cares-text-label)' }}>{product.active_versions}</td>
                      <td style={{ padding: '12px', fontSize: '13px', color: 'var(--cares-text-label)', fontStyle: 'italic' }}>{product.recommendation}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ProductGovernanceDashboard;
